import { CLEARANCE, type Rect, SEAT_CITIES } from "./panel-marks";
import type { CityName, DotField, Point } from "./us-dot-field";

export interface Seat {
  city: CityName;
  side: "left" | "right";
  drop: "above" | "below";
  /** Panel-space rect the mark itself will occupy. */
  rect: Rect;
  pin: Point;
}

export interface SeatQuery {
  field: DotField;
  /** Scale from comp units to panel pixels. */
  k: number;
  panel: { width: number; height: number };
  /**
   * Copy ink to keep clear of, in panel pixels. Measured off the rendered
   * text, so the marks dodge whatever the responsive type ramp produced rather
   * than what the comp assumed.
   */
  copy: Rect[];
}

const grow = (r: Rect, m: number): Rect => ({
  x: r.x - m,
  y: r.y - m,
  width: r.width + 2 * m,
  height: r.height + 2 * m,
});

export const overlaps = (a: Rect, b: Rect): boolean =>
  Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) > 0 &&
  Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) > 0;

/**
 * Every seat a mark of this size could take: each candidate city crossed with
 * the four ways a mark can hang off its pin, filtered down to the ones that
 * clear the copy, the panel edge and the fixed lit cells. Computed against the
 * live field, so the pool is correct at any panel size.
 */
export function seatsFor(
  q: SeatQuery,
  markWidth: number,
  markHeight: number,
  leaderLength: number,
  inset: number,
): Seat[] {
  const { k, panel } = q;
  const w = markWidth * k;
  const h = markHeight * k;
  const gap = (leaderLength + 4) * k;
  const zones = q.copy.map((z) => grow(z, CLEARANCE.copy * k));
  const edge = CLEARANCE.edge * k;

  const out: Seat[] = [];
  for (const city of SEAT_CITIES) {
    const pin = q.field.cities[city];
    if (!pin) {
      continue;
    }
    for (const side of ["right", "left"] as const) {
      for (const drop of ["above", "below"] as const) {
        const rect: Rect = {
          x: side === "right" ? pin.x - inset * k : pin.x + inset * k - w,
          y: drop === "above" ? pin.y - gap - h : pin.y + gap,
          width: w,
          height: h,
        };
        if (
          rect.x < edge ||
          rect.y < edge ||
          rect.x + rect.width > panel.width - edge ||
          rect.y + rect.height > panel.height - edge
        ) {
          continue;
        }
        if (zones.some((z) => overlaps(rect, z))) {
          continue;
        }
        // The leader and pin sit outside the mark, so they need checking too.
        const tail: Rect = {
          x: pin.x - 2,
          y: drop === "above" ? pin.y - gap : pin.y,
          width: 4,
          height: gap,
        };
        if (zones.some((z) => overlaps(tail, z))) {
          continue;
        }
        out.push({ city, side, drop, rect, pin });
      }
    }
  }
  return out;
}
